import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { selectIncompleteUploadedFileIds } from '@/uppy-file-upload/store/selectors'
import { selectCurrentFolderProcessingFileIds } from '@/user_files/files/store/selectors'

/**
 * Combines all user file polling sources into a single list of file ids for a single centralized file polling processor.
 * Current sources include:
 * - Uppy/user-files uploads still pending or processing
 * - Files in the currently loaded folder still processing
 */
export const selectCombinedUserFilePollingIds = createSelector(
  [selectIncompleteUploadedFileIds, selectCurrentFolderProcessingFileIds, (state: RootState) => state.userFilesPollingState.sourcesById],
  (drawerFileIds, currentFolderFileIds, sourcesById): string[] => {
    const seenIds = new Set<string>()
    const dedupedIds: string[] = []

    for (const fileId of [...drawerFileIds, ...currentFolderFileIds]) {
      if (seenIds.has(fileId)) continue
      seenIds.add(fileId)
      dedupedIds.push(fileId)
    }

    for (const { fileIds } of Object.values(sourcesById)) {
      for (const fileId of fileIds) {
        if (seenIds.has(fileId)) continue
        seenIds.add(fileId)
        dedupedIds.push(fileId)
      }
    }

    return dedupedIds
  }
)

export const selectUserFilesPollingStats = createSelector(
  [(state: RootState) => state.userFilesPollingState, selectCombinedUserFilePollingIds],
  (pollingState, fileIds) => ({
    active: pollingState.isRunning && fileIds.length > 0,
    pollCount: pollingState.pollCount,
    lastPollTimestamp: pollingState.lastPollTimestamp,
  })
)
